import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  map,
  Observable,
  switchMap,
  of,
} from 'rxjs';
import {
  AIMode,
  ConversationChannel,
  Participant,
  ParticipantType,
  PlatformLocation,
  MessageType,
  MetadataIdentifier,
} from '@vendasta/conversation';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_PLATFORM_LOCATION_TOKEN,
  PARTNER_ID_TOKEN,
  USER_ID_TOKEN,
} from '../tokens';
import { toFirestoreId, fromFirestoreId } from '../conversation-utils';
import { ConversationService } from './conversation.service';
import { ConversationStatelessService } from '../conversation-stateless.service';
import { v4 as uuidv4 } from 'uuid';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AiAssistantService,
  VENDASTA_AI_AVATAR_SVG_ICON,
  ASSISTANT_ID_REPUTATION_SPECIALIST,
  ASSISTANT_ID_AI_SEARCH_EXPERT,
  SYSTEM_ASSISTANT_ID,
  aiAssistantAssetUrl,
} from '@galaxy/ai-assistant';
import { ConversationAvailableChannels } from '../interface/conversation.interface';
import { DEFAULT_OPENAI_BOT_NAME, CONVERSATION_COOKIE_PREFIX } from '../inbox.constants';
import { ConversationDetail, MessageInfo } from '../interface/conversation.interface';
import { buildAIContextMetadata } from '../conversation-utils';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';

export interface SuggestionChip {
  label: string;
  message: string;
  key: string;
}

export const SEARCH_EXPERT_CHIP_EVENT_NAMES: Record<string, string> = {
  'INBOX.AI_CHAT.SUGGESTIONS.SEARCH.BRAND_VISIBILITY_TEXT': 'Brand Visibility Open beta',
  'INBOX.AI_CHAT.SUGGESTIONS.SEARCH.SEO_AUDIT_TEXT': 'SEO Audit Open beta',
  'INBOX.AI_CHAT.SUGGESTIONS.SEARCH.AEO_AUDIT_TEXT': 'AEO Audit Open beta',
};

export function searchExpertChipEventName(chipKey: string, assistantId: string | undefined): string | null {
  if (assistantId !== ASSISTANT_ID_AI_SEARCH_EXPERT) {
    return null;
  }
  return SEARCH_EXPERT_CHIP_EVENT_NAMES[chipKey] ?? null;
}

@Injectable()
export class AiAssistantConversationService {
  private readonly conversationService = inject(ConversationService);
  private readonly conversationStatelessService = inject(ConversationStatelessService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly aiAssistantService = inject(AiAssistantService);
  private readonly translateService = inject(TranslateService);
  private readonly cookieService = inject(CookieService);
  private readonly document = inject(DOCUMENT);

  private readonly userId$ = inject(USER_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly accountGroupId$ = inject(ACCOUNT_GROUP_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly partnerId$ = inject(PARTNER_ID_TOKEN).pipe(distinctUntilChanged());
  private readonly senderParticipant$: Observable<Participant>;
  private readonly platformLocation = inject(CONVERSATION_PLATFORM_LOCATION_TOKEN);

  private readonly aiAssistantId$$ = new BehaviorSubject<string | null>(null);
  readonly aiAssistantId$ = this.aiAssistantId$$.pipe(distinctUntilChanged());

  readonly assistantId = toSignal(this.aiAssistantId$);

  readonly currentAssistant = toSignal(
    combineLatest([this.aiAssistantId$, this.partnerId$, this.accountGroupId$]).pipe(
      switchMap(([assistantId, partnerId, accountGroupId]) => {
        if (!assistantId) {
          return of(null);
        }
        return this.aiAssistantService.getHydratedAssistant(partnerId, accountGroupId, assistantId, {
          skipGoalsHydration: true,
        });
      }),
    ),
    { initialValue: null },
  );

  readonly dynamicAssistantName = computed(() => {
    const hydratedAssistant = this.currentAssistant();
    return hydratedAssistant?.assistant?.name || null;
  });

  readonly assistantIconName = computed(() => {
    const hydratedAssistant = this.currentAssistant();
    return hydratedAssistant?.decoration?.defaultAvatarIcon || VENDASTA_AI_AVATAR_SVG_ICON;
  });

  readonly assistantAvatarUrl = computed(() => {
    const hydratedAssistant = this.currentAssistant();
    return (
      hydratedAssistant?.assistant?.avatarUrl ||
      hydratedAssistant?.decoration?.avatarIconUrl ||
      aiAssistantAssetUrl(this.assistantIconName(), this.document) ||
      undefined
    );
  });

  readonly isSystemAssistant = computed(() => {
    const hydratedAssistant = this.currentAssistant();
    return !!hydratedAssistant && hydratedAssistant.assistant?.id === SYSTEM_ASSISTANT_ID;
  });

  readonly isAssistantLoaded = computed(() => {
    const hydratedAssistant = this.currentAssistant();
    return !!hydratedAssistant?.assistant?.id;
  });

  readonly availableChannels: ConversationAvailableChannels = {
    availableChannels: [ConversationChannel.CONVERSATION_CHANNEL_AI_ASSISTANT],
    preferredChannel: ConversationChannel.CONVERSATION_CHANNEL_AI_ASSISTANT,
    channelAvailabilities: [
      {
        channel: ConversationChannel.CONVERSATION_CHANNEL_AI_ASSISTANT,
        isAvailable: true,
      },
    ],
  };

  readonly DEFAULT_OPENAI_BOT_NAME = DEFAULT_OPENAI_BOT_NAME;

  constructor() {
    this.senderParticipant$ = combineLatest([this.userId$, this.accountGroupId$, this.partnerId$]).pipe(
      map(([user, agid, partnerID]) => {
        return new Participant({
          participantType: ParticipantType.PARTICIPANT_TYPE_IAM_USER,
          partnerId: partnerID,
          accountGroupId: agid,
          internalParticipantId: user,
          isSubjectParticipant: true,
        });
      }),
    );
  }

  setAssistantId(assistantId: string | null): void {
    this.aiAssistantId$$.next(assistantId);
  }

  async startConversation(): Promise<void> {
    const assistantId = await firstValueFrom(this.aiAssistantId$);
    if (!assistantId) {
      this.snackbarService.openErrorSnack('INBOX.ERROR.UNABLE_TO_CREATE_CONVERSATION');
      return;
    }

    const sender = await firstValueFrom(this.senderParticipant$);
    const partnerId = await firstValueFrom(this.partnerId$);
    const agid = await firstValueFrom(this.accountGroupId$);

    const aiParticipant = new Participant({
      participantType: ParticipantType.PARTICIPANT_TYPE_AI_ASSISTANT,
      partnerId: partnerId,
      accountGroupId: agid,
      internalParticipantId: assistantId,
      isSubjectParticipant: true,
    });

    try {
      const resp = await firstValueFrom(
        this.conversationStatelessService.createConversation(
          [sender, aiParticipant],
          ConversationChannel.CONVERSATION_CHANNEL_AI_ASSISTANT,
          this.platformLocation as PlatformLocation,
          uuidv4(),
        ),
      );

      this.conversationService.setCurrentFirestoreConversationId(
        toFirestoreId(resp?.conversation?.conversationId ?? ''),
      );
    } catch {
      this.snackbarService.openErrorSnack('INBOX.ERROR.UNABLE_TO_CREATE_CONVERSATION');
    }
  }

  private getSuggestionChipsFromKeys(keys: string[]): SuggestionChip[] {
    return keys.map(
      (key): SuggestionChip => ({
        label: this.translateService.instant(key),
        message: this.translateService.instant(key),
        key,
      }),
    );
  }

  readonly aiSuggestionChips = computed(() => {
    const assistant = this.currentAssistant();
    if (!assistant?.assistant?.id) {
      return [];
    }

    const suggestionKeysByAssistant: Record<string, string[]> = {
      [SYSTEM_ASSISTANT_ID]: [
        'INBOX.AI_CHAT.SUGGESTIONS.HELP_GETTING_STARTED',
        'INBOX.AI_CHAT.SUGGESTIONS.WALK_THROUGH_FEATURE',
        'INBOX.AI_CHAT.SUGGESTIONS.HELP_FIXING_SOMETHING',
      ],
      [ASSISTANT_ID_REPUTATION_SPECIALIST]: [
        'INBOX.AI_CHAT.SUGGESTIONS.REPUTATION.WHO_ARE_HAPPIEST_CUSTOMERS',
        'INBOX.AI_CHAT.SUGGESTIONS.REPUTATION.RECURRING_KEYWORDS',
        'INBOX.AI_CHAT.SUGGESTIONS.REPUTATION.FEEDBACK_TRENDS',
      ],
      [ASSISTANT_ID_AI_SEARCH_EXPERT]: Object.keys(SEARCH_EXPERT_CHIP_EVENT_NAMES),
    };

    const suggestionKeys = suggestionKeysByAssistant[assistant.assistant.id];
    return suggestionKeys ? this.getSuggestionChipsFromKeys(suggestionKeys) : [];
  });

  readonly aiTitle = computed(() => {
    const assistantName = this.dynamicAssistantName();
    return this.translateService.instant('INBOX.AI_CHAT.NO_MESSAGES.TITLE', { assistantName });
  });

  readonly aiDescription = computed(() => {
    const assistantName = this.dynamicAssistantName();

    const hasSuggestions = this.aiSuggestionChips().length > 0;
    const descriptionKey = hasSuggestions
      ? 'INBOX.AI_CHAT.NO_MESSAGES.DESCRIPTION_WITH_SUGGESTIONS'
      : 'INBOX.AI_CHAT.NO_MESSAGES.DESCRIPTION_WITHOUT_SUGGESTIONS';

    return this.translateService.instant(descriptionKey, { assistantName });
  });

  async sendMessage(
    messageInfo: MessageInfo,
    additionalMetadata?: {
      identifier: MetadataIdentifier;
      data: {
        [key: string]: string;
      };
    }[],
  ): Promise<string> {
    const currentFirestoreConversationId = await firstValueFrom(
      this.conversationService.currentFirestoreConversationId$,
    );

    const baseMetadata = buildAIContextMetadata();
    const metadata = additionalMetadata ? [...baseMetadata, ...additionalMetadata] : baseMetadata;

    await this.conversationService.sendAndStageMessage(
      fromFirestoreId(currentFirestoreConversationId),
      MessageType.MESSAGE_TYPE_MESSAGE,
      messageInfo.text ?? '',
      messageInfo.channel as ConversationChannel,
      this.platformLocation as PlatformLocation,
      messageInfo.attachments,
      undefined,
      undefined,
      metadata,
      undefined,
      messageInfo.aiMode as AIMode,
    );

    return currentFirestoreConversationId;
  }

  private buildCookieKey(contextId: string, assistantId: string): string {
    return CONVERSATION_COOKIE_PREFIX + contextId + '_' + assistantId;
  }

  getAiConversationCookie(assistantId: string, contextId: string): string {
    if (!assistantId || !contextId) {
      return '';
    }
    return this.cookieService.get(this.buildCookieKey(contextId, assistantId));
  }

  setAiConversationCookie(conversationDetail: ConversationDetail, contextId: string): void {
    const assistantId = this.assistantId();
    if (!assistantId || !contextId) {
      return;
    }
    const aiParticipant = conversationDetail.participants.find(
      (p) =>
        (p.participantType === ParticipantType.PARTICIPANT_TYPE_AI_ASSISTANT ||
          p.participantType === ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT) &&
        p.internalParticipantId === assistantId,
    );
    if (!aiParticipant) {
      return;
    }
    const expiry = new Date();
    expiry.setTime(expiry.getTime() + 30 * 60 * 1000);

    this.cookieService.set(
      this.buildCookieKey(contextId, aiParticipant.internalParticipantId),
      toFirestoreId(conversationDetail.conversation.conversationId),
      expiry,
      '/',
    );
  }

  deleteAiConversationCookie(assistantId: string, contextId: string): void {
    if (!assistantId || !contextId) {
      return;
    }
    this.cookieService.delete(this.buildCookieKey(contextId, assistantId), '/');
  }
}
